// Learning Tracks mutations. All admin-gated; the server enforces the
// `admin` role on each write endpoint. Each hook patches/invalidates the
// shared QueryClient cache so the list and detail views update in lockstep
// without needing to thread state through props.

import { useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { useRouter } from '@tanstack/react-router';
import type { components } from '@leanin/api-client';
import { trackLearningTrackCreated } from '../analytics/events';
import { getApiClient } from '../api-client';
import { unwrap, unwrapVoid } from '../api-error';
import { restoreSnapshots, type SnapshotSet } from '../query-cache-utils';
import {
  learningTracksKeys,
  type LearningTrackCursorPage,
  type LearningTrackDetail,
  type LearningTrackSummary,
} from '../queries/learning-tracks';

// ---- Inputs ----

export type CreateLearningTrackInput =
  components['schemas']['CreateLearningTrackRequest'];

export type UpdateLearningTrackInput =
  components['schemas']['UpdateLearningTrackRequest'];

// ---- Track-level mutations ----

export function useCreateLearningTrack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      input: CreateLearningTrackInput,
    ): Promise<LearningTrackSummary> => {
      const client = getApiClient();
      return unwrap(
        await client.POST('/api/v1/admin/learning-tracks', {
          body: input,
        }),
      );
    },
    onSuccess: () => {
      // Tracks are created empty and resources attached afterwards, so the
      // count is 0 by construction at this point.
      trackLearningTrackCreated({ resource_count: 0 });
      qc.invalidateQueries({ queryKey: learningTracksKeys.lists() });
    },
  });
}

export function useUpdateLearningTrack() {
  const qc = useQueryClient();
  const router = useRouter();
  return useMutation({
    mutationFn: async (input: {
      readonly id: string;
      readonly patch: UpdateLearningTrackInput;
    }): Promise<LearningTrackSummary> => {
      const client = getApiClient();
      return unwrap(
        await client.PATCH('/api/v1/admin/learning-tracks/{id}', {
          params: { path: { id: input.id } },
          body: input.patch,
        }),
      );
    },
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: learningTracksKeys.detail(vars.id).queryKey });
      qc.invalidateQueries({ queryKey: learningTracksKeys.lists() });
      // The tab is titled from the track page's loader, which a save on the
      // page it titles does not rerun by itself. The router reloads only when
      // it shows this track, and marks only this track's pages invalid, so a
      // save keeps other pages' preloads and cached matches. A page visited
      // later reads the invalidated query through its own loader.
      const titlesTrack = (match: (typeof router.state.matches)[number]) =>
        (match.routeId === '/_authed/admin/resources/learning-tracks/$trackId/' ||
          match.routeId === '/_public/resources/learning-tracks/$trackId/') &&
        match.params.trackId === vars.id;
      if (router.state.matches.some(titlesTrack)) {
        void router.invalidate({ filter: titlesTrack });
      }
    },
  });
}

export function useDeleteLearningTrack() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string): Promise<string> => {
      const client = getApiClient();
      unwrapVoid(
        await client.DELETE('/api/v1/admin/learning-tracks/{id}', {
          params: { path: { id } },
        }),
      );
      return id;
    },
    onSuccess: (_data, id) => {
      qc.removeQueries({ queryKey: learningTracksKeys.detail(id).queryKey });
      qc.invalidateQueries({ queryKey: learningTracksKeys.lists() });
    },
  });
}

const reorderTracksKey = [...learningTracksKeys.all, 'reorder'] as const;

// Lays a cached track list out in the chosen order. Pages are re-cut at their
// old sizes, so a track can move across a page boundary; a track the order
// doesn't name keeps its place relative to the others, after them.
export function reorderTrackPages(
  data: InfiniteData<LearningTrackCursorPage>,
  orderedTrackIds: readonly string[],
): InfiniteData<LearningTrackCursorPage> {
  const position = new Map(orderedTrackIds.map((id, index) => [id, index]));
  const rank = (track: LearningTrackSummary) =>
    position.get(track.id) ?? orderedTrackIds.length;
  const ordered = data.pages
    .flatMap((page) => page.items)
    .map((track, index) => ({ track, index }))
    .sort((a, b) => rank(a.track) - rank(b.track) || a.index - b.index)
    .map(({ track }) => {
      const displayOrder = position.get(track.id);
      return displayOrder === undefined ? track : { ...track, displayOrder };
    });
  let start = 0;
  return {
    ...data,
    pages: data.pages.map((page) => {
      const items = ordered.slice(start, start + page.items.length);
      start += page.items.length;
      return { ...page, items };
    }),
  };
}

export function useReorderLearningTracks() {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: reorderTracksKey,
    // Each save carries the whole order, so saves run one at a time in the
    // order the moves were made: run concurrently, an earlier move reaching
    // the server last would overwrite a later one.
    scope: { id: 'learning-track-order' },
    mutationFn: async (orderedTrackIds: readonly string[]): Promise<void> => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/admin/learning-tracks/reorder', {
          body: { orderedTrackIds: [...orderedTrackIds] },
        }),
      );
    },
    // Runs as soon as the move is made, even while an earlier save holds the
    // queue, so every cached track list shows the chosen order straight away.
    onMutate: async (orderedTrackIds): Promise<SnapshotSet> => {
      await qc.cancelQueries({ queryKey: learningTracksKeys.lists() });
      const previousLists = qc.getQueriesData<InfiniteData<LearningTrackCursorPage>>({
        queryKey: learningTracksKeys.lists(),
      });
      qc.setQueriesData<InfiniteData<LearningTrackCursorPage>>(
        { queryKey: learningTracksKeys.lists() },
        (data) => (data === undefined ? data : reorderTrackPages(data, orderedTrackIds)),
      );
      return { previousLists };
    },
    // With later moves still queued, their order stays on screen; the refetch
    // after the last save puts back whatever the server holds.
    onError: (_error, _orderedTrackIds, snapshots) => {
      if (qc.isMutating({ mutationKey: reorderTracksKey }) === 1) {
        restoreSnapshots(qc, snapshots);
      }
    },
    // Refetched after a failure too: a save rejected because the set of tracks
    // changed would otherwise be retried from the same stale list. Only the
    // last queued save refetches; an earlier refetch would overwrite the order
    // a later move has already put on screen.
    onSettled: () => {
      if (qc.isMutating({ mutationKey: reorderTracksKey }) === 1) {
        void qc.invalidateQueries({ queryKey: learningTracksKeys.lists() });
      }
    },
  });
}

// ---- Item-level mutations ----

export function useAttachLearningTrackResource(trackId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      readonly resourceId: string;
      readonly sortOrder?: number;
    }): Promise<void> => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST('/api/v1/admin/learning-tracks/{id}/resources', {
          params: { path: { id: trackId } },
          body: {
            resourceId: input.resourceId,
            ...(input.sortOrder !== undefined
              ? { sortOrder: input.sortOrder }
              : {}),
          },
        }),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: learningTracksKeys.detail(trackId).queryKey,
      });
      qc.invalidateQueries({ queryKey: learningTracksKeys.lists() });
    },
  });
}

export function useDetachLearningTrackResource(trackId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (resourceId: string): Promise<void> => {
      const client = getApiClient();
      unwrapVoid(
        await client.DELETE(
          '/api/v1/admin/learning-tracks/{id}/resources/{resourceId}',
          { params: { path: { id: trackId, resourceId } } },
        ),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: learningTracksKeys.detail(trackId).queryKey,
      });
      qc.invalidateQueries({ queryKey: learningTracksKeys.lists() });
    },
  });
}

export function useReorderLearningTrackResources(trackId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (
      orderedResourceIds: readonly string[],
    ): Promise<void> => {
      const client = getApiClient();
      unwrapVoid(
        await client.POST(
          '/api/v1/admin/learning-tracks/{id}/resources/reorder',
          {
            params: { path: { id: trackId } },
            body: { orderedResourceIds: [...orderedResourceIds] },
          },
        ),
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: learningTracksKeys.detail(trackId).queryKey,
      });
    },
  });
}

// Viewer-self progress: optimistic check/uncheck of one curriculum item.
// The detail query is the single source the page renders from, so the
// optimistic write patches it directly and rolls back on error.
export function useSetTrackItemProgress(trackId: string) {
  const qc = useQueryClient();
  const detailKey = learningTracksKeys.detail(trackId).queryKey;
  return useMutation<
    void,
    Error,
    { resourceId: string; completed: boolean },
    { previous: LearningTrackDetail | undefined }
  >({
    mutationFn: async ({ resourceId, completed }) => {
      const client = getApiClient();
      const path = {
        params: { path: { id: trackId, resourceId } },
      };
      unwrapVoid(
        completed
          ? await client.PUT(
              '/api/v1/learning-tracks/{id}/progress/{resourceId}',
              path,
            )
          : await client.DELETE(
              '/api/v1/learning-tracks/{id}/progress/{resourceId}',
              path,
            ),
      );
    },
    onMutate: async ({ resourceId, completed }) => {
      await qc.cancelQueries({ queryKey: detailKey });
      const previous = qc.getQueryData<LearningTrackDetail>(detailKey);
      if (previous) {
        const items = previous.items.map((item) =>
          item.resourceId === resourceId
            ? { ...item, viewerCompleted: completed }
            : item,
        );
        qc.setQueryData<LearningTrackDetail>(detailKey, {
          ...previous,
          items,
          viewerCompletedCount: items.filter((i) => i.viewerCompleted).length,
        });
      }
      return { previous };
    },
    onError: (_err, _vars, ctx) => {
      if (ctx?.previous) qc.setQueryData(detailKey, ctx.previous);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: detailKey });
    },
  });
}
